<script setup lang="ts">
// Modal de confirmação de resgate de recompensas do Minha Conta V2 (Figma
// 19771:42142, "Modal -> Emblemas"): ícone flutuando sobre um círculo,
// título/valor centralizados, um único CTA — sem etapa de confirmar/cancelar
// (o resgate já dispara na hora). Componente novo, só usado pelo
// ScreenRewards.vue novo — a tela antiga de recompensas continua com o modal
// legado (components/user/rewards/Redeem.vue).
import {
  parseRewardDescription,
  isRewardAppOnlyBlocked,
} from '@/utils/rewards';

type Props = {
  reward: Reward | null;
  overlayClass?: string;
};

const { reward, overlayClass = '' } = defineProps<Props>();

const isOpen = defineModel<boolean>({ required: true });

// `play`: reemitido pra quem embute esta tela num painel próprio (o sheet de
// Rewards do Clube Fidelidade) — mesmo motivo do modal legado
// (components/user/rewards/Redeem.vue).
const emit = defineEmits<{ redeemed: []; play: [] }>();

const isLoadingRedeem = ref(false);
const isRedeemSuccess = ref(false);

const { redeemReward } = useRewards();

const { openToaster } = useLayoutStore();
const { showMoneyFormat } = useWallet();
const { normalizeThumbnail, getThumbnailPathSet } = useCasino();
// Cartaz próprio do bônus app-only (`components/app-download/BonusModal.vue`),
// não o genérico do menu lateral (ENG-495). O z-[9998] dele fica acima do
// z-[100] deste, então abre por cima.
const appDownload = useAppDownloadBonusStore();

// ENG-799: com o botão do card travado este modal não abre pelo caminho
// normal. Existe pra abertura programática — e porque o guard do
// `redeemReward()` engoliria o clique em silêncio, o que daria um CTA morto.
const isAppOnlyBlocked = computed(
  () => !!reward && isRewardAppOnlyBlocked(reward),
);

// mesmo fallback do card da lista (RewardCard.vue) — cifrão fixo pra quando a
// recompensa não tem imagem de jogo. getAssetsUrlForCDN() é obrigatório aqui:
// path cru funciona local (Nuxt serve /public/* no mesmo domínio) mas dá 404
// em prod (assets ficam num CDN separado) — mesmo bug já corrigido no card.
const FALLBACK_IMAGE = '/images/user-v2/rewards/reward-dollar.png';

const thumbnailPath = computed(() =>
  reward?.game_image ? `${normalizeThumbnail(reward.game_image)}/public` : '',
);

const thumbnailPathSet = computed(() =>
  reward?.game_image ? getThumbnailPathSet(reward.game_image) : '',
);

const parsedDescription = computed(() =>
  parseRewardDescription(reward?.player_desc),
);

// frase de marketing ("A recompensa X concede a você Y") — o Figma tem uma
// versão fixa pro exemplo ("...a cada 15 dias"), mas o Reward não tem campo
// de recorrência; aqui ela é montada por tipo, com os dados reais de
// valor/quantidade de cada item.
const dynamicDescription = computed(() => {
  if (!reward) return '';

  const name = parsedDescription.value.description;
  const value =
    parsedDescription.value.value ||
    (reward.amount ? showMoneyFormat({ value: reward.amount / 100 }) : '');

  if (reward.type === 'freespins') {
    return `A recompensa ${name} concede a você ${reward.quantity ?? ''} rodadas grátis.`;
  }

  if (reward.type === 'freebets') {
    return `A recompensa ${name} concede a você ${reward.quantity ?? ''} apostas grátis.`;
  }

  if (reward.type === 'money_bonus') {
    return `A recompensa ${name} concede a você ${value} em saldo bônus.`;
  }

  if (reward.type === 'money_cashback') {
    return `A recompensa ${name} concede a você ${value} em cashback.`;
  }

  if (reward.type === 'money_real') {
    return `A recompensa ${name} concede a você ${value} em saldo real.`;
  }

  return name;
});

// trava o scroll de fundo enquanto o modal está aberto — mesmo composable
// contador do Modal.vue (useBodyScrollLock), em vez de mexer no
// document.body.style direto: garante destrave mesmo se este modal algum
// dia for reaproveitado fora do Shell (que hoje já trava o scroll por fora).
useBodyScrollLock(isOpen);

async function handleRedeemReward() {
  if (!reward) {
    return;
  }

  isLoadingRedeem.value = true;

  await redeemReward(reward.id)
    .then((response) => {
      if (response?.data?.success) {
        isRedeemSuccess.value = true;
        emit('redeemed');

        return;
      }

      // 2xx com `success: false` NÃO cai no catch. Sem este ramo o modal
      // apagava o loading e não dizia nada — foi assim que a recusa do
      // backend ("só pode ser resgatado pelo aplicativo") ficou invisível.
      // O corpo traz a razão em `message`; `user_message` é o campo que o
      // catch já usava, fica de alternativa.
      openToaster({
        message:
          response?.data?.message ||
          response?.data?.user_message ||
          'Não foi possível resgatar a recompensa. Tente de novo.',
        type: 'error',
      });
    })
    .catch((error: any) => {
      openToaster({
        message:
          error?.data?.message ||
          error?.data?.user_message ||
          'Erro ao resgatar recompensa',
        type: 'error',
      });
    })
    .finally(() => {
      isLoadingRedeem.value = false;
    });
}

function close() {
  isOpen.value = false;
  isRedeemSuccess.value = false;
}

// "Jogar Agora" navega pra fora do painel (jogo ou /sports) — sem isso, o
// modal E o painel V2 inteiro ficavam abertos por cima da tela de destino,
// já que o NuxtLink só troca de rota, não fecha nada.
const { close: closeUserV2Modal } = useUserV2Modal();

function handlePlayNow() {
  close();
  closeUserV2Modal();
  emit('play');
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen && reward"
      :class="[
        overlayClass,
        'user-v2-reward-redeem-modal fixed inset-0 z-[100] flex items-end justify-center font-sans lg:items-center lg:p-[16px]',
      ]"
      @dragstart.prevent
    >
      <!-- clique fora cai neste backdrop (cobre o wrapper), não no wrapper —
           @click.self no pai nunca disparava -->
      <div
        class="modal-backdrop absolute inset-0 bg-black/70"
        @click="close"
      />

      <!-- mobile: bottom sheet full-width, cantos só no topo; desktop: card
           centralizado com cantos nos 4 lados (igual ao Figma, node
           19771:42400 — mobile ocupa a base e as laterais inteiras). -->
      <div
        class="modal-card relative flex w-full flex-col items-center gap-[24px] rounded-t-[20px] bg-v2-background px-[16px] pb-[56px] pt-[104px] text-center lg:max-w-[560px] lg:rounded-[20px] lg:pb-[24px]"
      >
        <BaseLoading
          class="!bg-v2-background"
          :show="isLoadingRedeem"
        />

        <UserV2ModalCloseButton @close="close" />

        <UserRewardsRedeemSuccess
          v-if="isRedeemSuccess"
          :selected-reward="reward"
          @close="close"
          @play="handlePlayNow"
        />

        <template v-else>
          <!-- selo flutuando por cima da borda superior do card, igual ao
               Figma (círculo 160px, metade pra fora/metade pra dentro) -->
          <div
            class="absolute left-1/2 top-[-80px] flex size-[160px] -translate-x-1/2 items-center justify-center overflow-hidden rounded-full border border-v2-default-300 bg-v2-default-200"
          >
            <img
              alt=""
              class="size-[100px] rounded-[11px] object-contain"
              :src="thumbnailPath || getAssetsUrlForCDN(FALLBACK_IMAGE)"
              :srcset="thumbnailPathSet || undefined"
            />
          </div>

          <div class="flex flex-col gap-[6px]">
            <p class="text-[18px] font-bold leading-[28px] text-white">
              {{ parsedDescription.description }}
            </p>

            <p class="text-[14px] leading-[20px] text-v2-muted">
              {{ dynamicDescription }}
            </p>
          </div>

          <!-- ENG-799: bloqueado vira convite pra baixar o app; sem link
               configurado, só o aviso (nada de CTA que não leva a lugar
               nenhum). -->
          <template v-if="isAppOnlyBlocked">
            <p
              class="border-solid w-full rounded-[12px] border border-v2-gold/30 bg-v2-gold/10 px-[16px] py-[10px] text-[12px] font-extrabold tracking-[0.4px] text-v2-gold"
            >
              {{ $t('rewards.app_only.badge') }}
            </p>

            <button
              class="flex h-[40px] w-full items-center justify-center rounded-[12px] bg-v2-gold px-[16px] text-[14px] font-bold text-v2-background"
              type="button"
              @click="appDownload.open()"
            >
              {{ $t('rewards.app_only.cta') }}
            </button>
          </template>

          <button
            v-else
            class="flex h-[40px] w-full items-center justify-center rounded-[12px] bg-v2-primary px-[16px] text-[14px] text-v2-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="isLoadingRedeem"
            type="button"
            @click="handleRedeemReward"
          >
            Resgatar recompensa
          </button>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
// main.scss reseta `transition` no app inteiro — animação de entrada via
// @keyframes/animation (mesma estratégia do AchievementModal.vue)
.modal-backdrop {
  animation: reward-redeem-fade 0.18s ease-out;
}

.modal-card {
  animation: reward-redeem-pop 0.2s ease-out;

  // mobile: sheet sobe da borda inferior (bate com o items-end do
  // container); desktop mantém o pop com scale do card centralizado.
  @media (max-width: 1023px) {
    animation-name: reward-redeem-slide-up;
  }
}

@keyframes reward-redeem-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes reward-redeem-pop {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes reward-redeem-slide-up {
  from {
    opacity: 0;
    transform: translateY(100%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
